import { useState } from "react";
import { Lock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").join(" ");
  return String(detail);
}

export default function AdminLogin({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      let data = {};
      try {
        data = await res.json();
      } catch (_) {
        data = {};
      }
      if (!res.ok) {
        const msg =
          (data && data.detail && formatError(data.detail)) ||
          (res.status === 401 ? "Incorrect password" : "Login failed. Please try again.");
        throw new Error(msg);
      }
      if (!data.token) throw new Error("Login failed. Please try again.");
      onSuccess(data.token);
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login"
      className="min-h-screen flex items-center justify-center bg-[var(--bg-soft)] px-6"
    >
      <div className="w-full max-w-md bg-white border border-[var(--gold-line)] p-10 lg:p-12">
        <div className="flex flex-col items-center text-center mb-10">
          <span className="w-12 h-12 rounded-full border border-[var(--gold-line)] flex items-center justify-center text-[var(--gold-deep)] mb-6">
            <Lock size={18} strokeWidth={1.5} />
          </span>
          <p className="font-marcellus uppercase tracking-[0.42em] text-[0.62rem] text-[var(--gold-deep)] mb-3">
            Private
          </p>
          <h1 className="font-couture text-3xl text-[var(--ink)] tracking-[-0.01em]">
            Analytics Console
          </h1>
          <p className="mt-3 font-couture-italic text-base text-[var(--ink-soft)]">
            Dr. P. B. Boss — visitor insights
          </p>
        </div>

        <form onSubmit={submit} className="space-y-5">
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            autoFocus
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            className="w-full border border-[var(--gold-line)] bg-white px-4 py-3.5 font-marcellus tracking-wide text-base text-[var(--ink)] outline-none focus:border-[var(--gold-deep)] transition-colors"
          />
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-700 font-marcellus">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading || !password}
            className="btn-couture w-full justify-center disabled:opacity-50"
          >
            {loading ? "Verifying…" : "Enter Console"}
          </button>
        </form>
      </div>
    </div>
  );
}
